import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { subMinutes, isBefore, isAfter, differenceInMilliseconds } from 'date-fns';
import { Button, Dropdown, Space } from 'antd';
import { DownOutlined, SendOutlined } from 'antd/icons';
import { FaPhoneAlt } from 'react-icons/fa';
import { MdVideocam } from 'react-icons/md';
import useUser from '@vl-core/hooks/useUser';
import { useRuntimeConfig } from 'vl-core/useRuntimeConfig';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import { Appointment } from 'vl-common/src/schemas/shared';
import useSessionType from '@hooks/useSessionType';
import useCallStatusUpdaters from '@hooks/useCallStatusUpdaters';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import useCallStatus from '@hooks/useCallStatus';
import { useSessionFormState } from '@hooks/useSessionFormState';
import usePatientDetails from '@hooks/usePatientDetails';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import useOtherPartyStatus from '@hooks/useOtherPartyStatus';
import { useVideoCallSignallingType } from '@hooks/useVideoCallSignallingType';
import { confirmationModal, errorModal } from 'vl-common/src/components/Modals';
import useMediaPermissions from '@hooks/useMediaPermissions';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { useTelValidation } from '@hooks/useTelValidation';
import useIsAutomationTestUser from 'vl-common/src/hooks/useIsAutomationTestUser';
import { useDPACheck } from '@hooks/useDPACheck';

export const useEarliestStartTime = () => {
  const { VIDEO_START_BUFFER } = useRuntimeConfig();
  const { appointment } = useSessionAppointment();

  return subMinutes(appointment.start, Number(VIDEO_START_BUFFER) || 0);
};

export const useIsBeforeCallCanStart = () => {
  const earliestStartTime = useEarliestStartTime();

  return isBefore(new Date(), earliestStartTime);
};

export const useLatestStartTime = () => {
  const { appointment } = useSessionAppointment();
  const patientStatus = useOtherPartyStatus(appointment);

  const additionalStartTime = patientStatus === 'connected' ? 10 * 60 * 1000 : 0;

  const latestStartTime = appointment.start ? new Date(appointment.start.toISOString()) : new Date();
  const currentEndTime = appointment.end ? new Date(appointment.end.toISOString()) : new Date();
  const duration = currentEndTime.getTime() - latestStartTime.getTime();

  latestStartTime?.setTime(latestStartTime.getTime() + duration + additionalStartTime);

  return latestStartTime;
};

export const useIsAfterCallCanStart = () => {
  const latestStartTime = useLatestStartTime();

  return latestStartTime ? isAfter(new Date(), latestStartTime) : false;
};

export const useIsVideoDisabled = () => {
  const user = useUser();
  const isAutomationTestUser = useIsAutomationTestUser();

  const { appointment } = useSessionAppointment();
  const { clinician_user_guid, appointment_completed: isCompleted } = appointment;

  const { updatersReady } = useCallStatusUpdaters(appointment);
  const sessionType = useSessionType();

  const isUsersAppointment = clinician_user_guid === user?.user_guid;
  const isBeforeCallCanStart = useIsBeforeCallCanStart();
  const isAfterCallCanStart = useIsAfterCallCanStart();

  const isInCallWindow = !isBeforeCallCanStart && !isAfterCallCanStart;

  return (
    !updatersReady ||
    sessionType !== 'VIDEO' ||
    isCompleted ||
    !isUsersAppointment ||
    (!isInCallWindow && !isAutomationTestUser)
  );
};

export const useIsAudioDisabled = () => {
  const user = useUser();

  const { appointment } = useSessionAppointment();
  const { clinician_user_guid, appointment_completed: isCompleted } = appointment;

  const isUsersAppointment = clinician_user_guid === user?.user_guid;

  return isCompleted || !isUsersAppointment;
};

const CallMenu = ({
  onCallPatient,
  audioCallUI
}: {
  onCallPatient: (appointment: Appointment, callPrimary: boolean) => void;
  audioCallUI: boolean;
}) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { requestPermissions, checkCurrentPermissions } = useMediaPermissions();

  const [, setRefresh] = useState(0);

  const [, setAlert] = useSessionFormState('alert');

  const user = useUser();
  const sessionType = useSessionType();
  const patientInfo = usePatientDetails();
  const { appointment } = useSessionAppointment();
  const appointment_id = useSessionAppointmentId();
  const { makeDPACheckRequired } = useDPACheck();

  const { detailsCurrentAppointment } = useAppointmentDetails(appointment_id);
  const isAppointmentV2 = detailsCurrentAppointment?.appt_version === 2;

  const callStatus = useCallStatus(appointment);
  const { dial, updatersReady } = useCallStatusUpdaters(appointment);

  const earliestStartTime = useEarliestStartTime();
  const latestStartTime = useLatestStartTime();

  const isBeforeCallCanStart = useIsBeforeCallCanStart();
  const isUsersAppointment = appointment?.clinician_user_guid === user?.user_guid;
  const isAfterCallCanStart = useIsAfterCallCanStart();
  const patientStatus = useOtherPartyStatus(appointment);
  const signallingType = useVideoCallSignallingType(appointment);

  const { isPrimaryTelValid, isSecondaryTelValid } = useTelValidation(appointment);

  const sendOneTimeLinkToPatient = async () => {
    const response = await dispatch(actions.sendOneTimeLink(appointment_id), false);

    if (response.type === actionTypes.ACTION_FAIL) {
      response?.error?.json().then((err: any) => {
        setAlert({
          type: 'error',
          message: `Unable to send one-time link to patient. Status: ${err?.message}`
        });
      });

      return;
    }

    setAlert({
      type: 'success',
      message: 'One-time link sent to patient'
    });
  };

  useEffect(() => {
    if (isBeforeCallCanStart) {
      // Refresh this component when the video call button should be enabled
      const videoStartBufferTimer = setTimeout(
        () => setRefresh((r) => r + 1),
        differenceInMilliseconds(earliestStartTime, new Date())
      );
      return () => clearInterval(videoStartBufferTimer);
    }
    return () => {};
  }, [earliestStartTime, isBeforeCallCanStart]);

  useEffect(() => {
    if (!isAfterCallCanStart) {
      // Refresh this component when the video call button should be disabled
      const videoEndBufferTimer = setTimeout(
        () => setRefresh((r) => r + 1),
        differenceInMilliseconds(latestStartTime, new Date())
      );
      return () => clearInterval(videoEndBufferTimer);
    }
    return () => {};
  }, [isAfterCallCanStart, latestStartTime]);

  const videoCallLabel = () => {
    if (updatersReady && !appointment.appointment_completed && callStatus === 'not-started') {
      return 'Video call';
    }

    if (appointment.appointment_completed || callStatus === 'completed') {
      return 'Video call (call completed)';
    }

    if (signallingType === 'dt') return 'Video call';

    return 'Video call (connecting ...)';
  };

  const oneTimeLinkDisabled =
    isAfterCallCanStart || // Current time is after call can start
    !isUsersAppointment || // This is not the current users appointment
    appointment?.appointment_completed || // The appointment is already completed
    callStatus === 'completed' || // The appointment is already completed
    patientInfo?.ident_prov_code !== 'COGNITO' || // Patient is not a cognito user
    sessionType !== 'VIDEO'; // Not a video appointment

  const videoDisabled =
    useIsVideoDisabled() || callStatus !== 'not-started' || (signallingType === 'dt' && patientStatus !== 'connected');
  const audioDisabled = useIsAudioDisabled();

  const ensureMediaPermissions = async (): Promise<void> => {
    let permissions = await checkCurrentPermissions();

    if (!permissions.audio || !permissions.video) {
      permissions = await requestPermissions();
    }

    if (!permissions.audio || !permissions.video) {
      throw new Error('Permission Denied');
    }
  };

  const onVideoCallPatient = () => {
    ensureMediaPermissions()
      .then(() => {
        dial().then(() => {
          dispatch(actions.sendPushNotification(appointment?.clinician_user_guid, appointment?.appointment_id));
          makeDPACheckRequired();
        });
      })
      .catch(() => {
        errorModal({
          title: t('Permission Denied'),
          content: t(
            "Could not connect to video camera and/or microphone. Please check your browser's permissions, adjust and refresh this page."
          )
        });
      });
  };

  return (
    <Dropdown
      menu={{
        items: [
          {
            key: 'video',
            icon: <MdVideocam style={{ color: videoDisabled ? 'rgba(0, 0, 0, 0.25)' : 'var(--productPrimary)' }} />,
            label: videoCallLabel(),
            disabled: videoDisabled,
            onClick: () => onVideoCallPatient()
          },
          {
            key: 'primary',
            icon: <FaPhoneAlt style={{ color: audioDisabled ? 'rgba(0, 0, 0, 0.25)' : 'var(--productPrimary)' }} />,
            label: (
              <Space>
                <span>{t('Call Primary Phone Number')}</span>
                {!isPrimaryTelValid && <ExclamationCircleFilled style={{ color: '#faad14', fontSize: '14px' }} />}
              </Space>
            ),
            disabled: audioCallUI || audioDisabled,
            onClick: () => {
              if (isPrimaryTelValid) {
                onCallPatient(appointment, true);
              } else {
                confirmationModal({
                  title: 'Number may be invalid',
                  content: `The number ${appointment?.patient_tel_mobile_no} may be invalid`,
                  okText: 'Call Anyway',
                  cancelText: 'Cancel',
                  onOk: () => onCallPatient(appointment, true)
                });
              }
            }
          },
          ...(appointment?.patient_tel_other_no
            ? [
                {
                  key: 'secondary',
                  icon: <FaPhoneAlt style={{ color: audioDisabled ? 'rgba(0, 0, 0, 0.25)' : '#eb2f96' }} />,
                  label: (
                    <Space>
                      <span>{t('Call Secondary Phone Number')}</span>
                      {!isSecondaryTelValid && (
                        <ExclamationCircleFilled style={{ color: '#faad14', fontSize: '14px' }} />
                      )}
                    </Space>
                  ),
                  disabled: audioCallUI || appointment.appointment_completed || !isUsersAppointment,
                  onClick: () => {
                    if (isSecondaryTelValid) {
                      onCallPatient(appointment, false);
                    } else {
                      confirmationModal({
                        title: 'Number may be invalid',
                        content: `The number ${appointment?.patient_tel_other_no} may be invalid`,
                        okText: 'Call Anyway',
                        cancelText: 'Cancel',
                        onOk: () => onCallPatient(appointment, false)
                      });
                    }
                  }
                }
              ]
            : []),
          {
            key: 'one-time-link',
            label: 'Send one-time video link',
            icon: (
              <SendOutlined style={{ color: oneTimeLinkDisabled ? 'rgba(0, 0, 0, 0.25)' : 'var(--productPrimary)' }} />
            ),
            disabled: oneTimeLinkDisabled,
            onClick: () => sendOneTimeLinkToPatient()
          }
        ]
      }}
      placement="bottom"
      trigger={['click']}
    >
      <Button type={isAppointmentV2 ? 'primary' : 'link'} data-testid="call-patient" disabled={audioCallUI}>
        {t('Call')}
        <DownOutlined />
      </Button>
    </Dropdown>
  );
};

export default CallMenu;
